import { Component, type ReactNode, type ErrorInfo } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Sentry, isSentryEnabled } from "@/lib/sentry";
import i18n from "@/lib/i18n";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log locally FIRST and unconditionally. Sentry is the only other sink, and
    // an ad blocker or private-browsing mode blocks its beacon — which is how a
    // render loop on /quest/:id reached users as a bare "Something went wrong"
    // with nothing in the console but a failed Sentry request.
    console.error("[ErrorBoundary]", error, errorInfo.componentStack);

    if (isSentryEnabled) {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo.componentStack ?? "" } },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-warning-500" />
          <h2 className="text-lg font-semibold text-text-primary">
            {i18n.t("error.somethingWentWrong")}
          </h2>
          <p className="max-w-sm text-sm text-text-tertiary">
            {i18n.t("error.unexpectedError")}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              this.setState({ hasError: false });
              window.location.reload();
            }}
          >
            {i18n.t("error.refreshPage")}
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
